import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from '../config';
import { Logo } from '../components/Icon';
import { Button, errorMessage, useToast } from '../components/ui';
import { api, type Me } from '../lib/api';
import { useAuth, useMe } from '../lib/auth';

const DOCS = ['terms', 'rules', 'prohibited', 'privacy'] as const;

/**
 * Согласие с пользовательским соглашением, правилами и политикой конфиденциальности.
 * Показывается при первом запуске и при изменении версии документов (app_settings.legalVersion).
 * Mini App показывает экран до начала работы; API дополнительно запрещает любые изменения без согласия
 * (403 CONSENT_REQUIRED), кроме смены языка, самого согласия и запроса на удаление данных.
 */
export function ConsentPage() {
  const { t } = useTranslation();
  const me = useMe();
  const { setMe } = useAuth();
  const toast = useToast();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const updated = Boolean(me.consent.acceptedVersion);

  const accept = async () => {
    setBusy(true);
    try {
      setMe(await api<Me>('/users/me/consent', { method: 'POST', json: { version: me.consent.requiredVersion } }));
    } catch (e) {
      toast(errorMessage(t, e), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="onboarding">
      <div className="brand brand-lg">
        <Logo size={48} />
        <span className="brand-name">{config.appName}</span>
      </div>
      <h1>{updated ? t('consent.updatedTitle') : t('consent.title')}</h1>
      <p className="muted">{t('consent.intro')}</p>
      <div className="list">
        {DOCS.map((d) => (
          <div key={d}>
            <button type="button" className="list-row" aria-expanded={open === d} onClick={() => setOpen(open === d ? null : d)}>
              <span className="list-row-label">{t(`help.${d}.title`)}</span>
              <span className="muted">{open === d ? '−' : '+'}</span>
            </button>
            {open === d ? <p className="consent-doc">{t(`help.${d}.text`)}</p> : null}
          </div>
        ))}
      </div>
      <label className="check-row">
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        <span>{t('consent.checkbox')}</span>
      </label>
      <Button size="lg" block disabled={!checked} loading={busy} onClick={() => void accept()}>
        {t('consent.accept')}
      </Button>
      <p className="tiny muted">{t('consent.version', { version: me.consent.requiredVersion })}</p>
    </div>
  );
}
